import { useState } from "react";
import { Clapperboard, Users, Youtube } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SKETCH_GROUPS, SKETCHES, STILLS } from "../data/content";

const STILLS_MAP = {
  "Dr. Cop Lawyer": "/assets/stills/still_1.jpg",
};

function YoutubeEmbed({ video, title, testId }) {
  const [play, setPlay] = useState(false);
  return (
    <div className="relative aspect-video overflow-hidden bg-black" data-testid={testId}>
      {play ? (
        <iframe
          src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
          title={title}
          className="h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <button onClick={() => setPlay(true)} className="group relative h-full w-full" aria-label={`Play ${title}`}>
          <img src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} alt={title} loading="lazy"
            className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="rounded-full bg-[#E11D48] p-4 shadow-[0_0_30px_rgba(225,29,72,0.6)] transition-transform group-hover:scale-110">
              <Youtube size={24} className="text-white" />
            </span>
          </span>
          <span className="absolute bottom-3 left-3 rounded-md bg-black/70 px-2 py-1 text-xs font-semibold text-white">{video.label}</span>
        </button>
      )}
    </div>
  );
}

function SketchCard({ sketch, index }) {
  const [showCredits, setShowCredits] = useState(false);
  const still = STILLS_MAP[sketch.title];
  const id = `sketch-${sketch.title.toLowerCase().replace(/[\s.—?]+/g, "-")}`;
  return (
    <Reveal delay={(index % 4) * 0.06}>
      <div className="group h-full rounded-xl border border-white/10 bg-[#181824]/70 overflow-hidden transition-all hover:border-[#F59E0B]/40 hover:-translate-y-1" data-testid={id}>
        {sketch.youtube && <YoutubeEmbed video={sketch.youtube} title={sketch.title} testId={`${id}-youtube`} />}
        {sketch.youtube2 && <YoutubeEmbed video={sketch.youtube2} title={`${sketch.title} — ${sketch.youtube2.label}`} testId={`${id}-youtube-2`} />}
        {!sketch.youtube && still && (
          <img src={still} alt={`Still from ${sketch.title}`} loading="lazy"
            className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105" />
        )}
        <div className="p-6">
          <div className="flex items-center gap-3">
            <Clapperboard size={22} className="text-[#E11D48] transition-transform group-hover:rotate-6 shrink-0" />
            {sketch.episode && (
              <span className="rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/40 px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-[#F59E0B]" data-testid={`${id}-episode`}>
                Episode {sketch.episode}
              </span>
            )}
          </div>
          <p className="mt-4 font-display text-2xl text-white group-hover:text-[#F59E0B] transition-colors">{sketch.title}</p>
          <p className="mt-3 text-sm text-gray-400 leading-relaxed">{sketch.description}</p>
          {sketch.credits && (
            <div className="mt-4">
              <button onClick={() => setShowCredits(!showCredits)} aria-expanded={showCredits} data-testid={`${id}-credits-toggle`}
                className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#F59E0B] link-gold">
                <Users size={13} /> {showCredits ? "Hide credits" : "Cast & credits"}
              </button>
              {showCredits && (
                <ul className="mt-3 space-y-1 text-xs text-gray-400" data-testid={`${id}-credits`}>
                  {sketch.credits.map((c) => (
                    <li key={c.name + c.role}><span className="text-gray-200">{c.name}</span> — {c.role}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </Reveal>
  );
}

export default function Sketches() {
  return (
    <main className="pt-16" data-testid="sketches-page">
      <Seo title="Sketches" description="Browse the Laugh at L.A. sketch lineup — Bathroom Attendant, Negotiating Actor, Dr. Cop Lawyer, What If? and more original scripted comedy." />
      <section className="spotlight-bg border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20 sm:py-24">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F59E0B]">Sketches</p>
            <h1 className="mt-3 font-display text-5xl sm:text-7xl text-white neon-red">The sketch engine.</h1>
            <p className="mt-6 max-w-2xl text-gray-300 leading-relaxed">
              Original scripted comedy that turns celebrity egos, office politics, and awkward encounters into punchlines. Recurring series appear together.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 space-y-16">
        {SKETCH_GROUPS.map((group) => (
          <div key={group.title} data-testid={`sketch-group-${group.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <Reveal className="flex items-center gap-4">
              <h2 className="font-display text-3xl sm:text-4xl text-white">{group.title}</h2>
              <span className="rounded-full bg-[#E11D48]/15 border border-[#E11D48]/40 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#E11D48]">{group.tag}</span>
            </Reveal>
            <div className="mt-6 grid sm:grid-cols-2 gap-5 items-start">
              {group.sketches.map((s, i) => <SketchCard key={s.title} sketch={s} index={i} />)}
            </div>
          </div>
        ))}

        <div data-testid="sketch-standalone">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl text-white">One-Offs & Parodies</h2>
          </Reveal>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {SKETCHES.map((s, i) => <SketchCard key={s.title} sketch={s} index={i} />)}
          </div>
        </div>
      </section>
    </main>
  );
}
